import { useState, type ReactNode } from 'react';
import { useOne, useGo, useCustomMutation, useInvalidate } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import {
  Loader2,
  CreditCard,
  User as UserIcon,
  ShoppingCart,
  Clock,
  ArrowLeftRight,
  ImageIcon,
  ExternalLink,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Undo2,
  Receipt,
  Paperclip,
  RefreshCw,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FormHeader } from '@/components/forms';
import {
  EntityAnalyticsCard,
  EntityAnalyticsGrid,
  TwoColumnLayout,
} from '@/components/shared';
import { PaymentProvider, PaymentStatus } from '@masters-club/types';
import type { MulticardPaymentPayload, Payment } from './types';
import { PaymentDocumentsUpload } from './components/PaymentDocumentsUpload';
import { PaymentDebugPanel } from './components/PaymentDebugPanel';
import { RefundDialog } from './components/RefundDialog';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PENDING: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  CONFIRMED: { label: 'Подтверждён', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  REJECTED: { label: 'Отклонён', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  REFUNDED: { label: 'Возврат', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  PARTIALLY_REFUNDED: { label: 'Частичный возврат', className: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
};

const PROVIDER_CONFIG: Record<string, { label: string; className: string }> = {
  MANUAL: { label: 'Ручной', className: 'bg-gray-500/10 text-gray-600 border-gray-500/20' },
  CLICK: { label: 'Click', className: 'bg-blue-500/10 text-blue-700 border-blue-500/20' },
  PAYME: { label: 'Payme', className: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20' },
  STRIPE: { label: 'Stripe', className: 'bg-violet-500/10 text-violet-700 border-violet-500/20' },
  MULTICARD: { label: 'Multicard', className: 'bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-500/20' },
  CLOUDPAYMENTS: { label: 'CloudPayments', className: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20' },
};

const PS_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'MasterCard',
  uzcard: 'UzCard',
  humo: 'Humo',
};

type TargetStatus = PaymentStatus;

interface PaymentActionConfig {
  target: TargetStatus;
  label: string;
  icon: ReactNode;
  buttonVariant: 'default' | 'destructive' | 'outline';
  buttonClassName?: string;
  dialogTitle: string;
  dialogDescription: string;
  confirmLabel: string;
  confirmClassName?: string;
  successMessage: string;
  errorMessage: string;
}

const REFUND_SUCCESS_MESSAGE: Partial<Record<PaymentProvider, string>> = {
  [PaymentProvider.MULTICARD]: 'Запрос на возврат отправлен. Статус обновится автоматически после ответа от Multicard.',
  [PaymentProvider.CLOUDPAYMENTS]: 'Запрос на возврат отправлен. Статус обновится автоматически после ответа от CloudPayments.',
};

function getRefundSuccessMessage(provider: PaymentProvider): string {
  return REFUND_SUCCESS_MESSAGE[provider] ?? 'Возврат оформлен';
}

const ALL_PAYMENT_ACTIONS: PaymentActionConfig[] = [
    {
      target: 'CONFIRMED',
      label: 'Подтвердить',
      icon: <CheckCircle2 className="h-4 w-4" />,
      buttonVariant: 'default',
      buttonClassName: 'bg-green-600 text-white hover:bg-green-700',
      dialogTitle: 'Подтвердить платёж?',
      dialogDescription:
        'Платёж будет подтверждён. Пользователь получит доступ только после ручного изменения статуса заказа.',
      confirmLabel: 'Подтвердить',
      confirmClassName: 'bg-green-600 text-white hover:bg-green-700',
      successMessage: 'Платёж подтверждён',
      errorMessage: 'Не удалось подтвердить платёж',
    },
    {
      target: 'REFUNDED',
      label: 'Возврат',
      icon: <Undo2 className="h-4 w-4" />,
      buttonVariant: 'outline',
      buttonClassName: 'border-destructive/30 text-destructive hover:bg-destructive/10',
      dialogTitle: 'Оформить возврат?',
      dialogDescription: 'Деньги будут возвращены клиенту. Это действие необратимо.',
      confirmLabel: 'Вернуть',
      confirmClassName: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      successMessage: 'Возврат оформлен',
      errorMessage: 'Не удалось оформить возврат',
    },
    {
      target: 'REJECTED',
      label: 'Отклонить',
      icon: <XCircle className="h-4 w-4" />,
      buttonVariant: 'destructive',
      dialogTitle: 'Отклонить платёж?',
      dialogDescription: 'Платёж будет помечен как отклонённый. Это необратимо.',
      confirmLabel: 'Отклонить',
      confirmClassName: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      successMessage: 'Платёж отклонён',
      errorMessage: 'Не удалось отклонить платёж',
    },
];

// Эквайринговые провайдеры: подтверждение/отклонение идут через webhook от
// провайдера, а не руками админа. Для них убираем ручные кнопки CONFIRM/REJECT,
// чтобы админ не мог рассинхронизировать локальный статус с провайдерским.
const ACQUIRING_PROVIDERS: ReadonlySet<PaymentProvider> = new Set([
  PaymentProvider.CLOUDPAYMENTS,
  PaymentProvider.MULTICARD,
  PaymentProvider.CLICK,
  PaymentProvider.PAYME,
  PaymentProvider.STRIPE,
]);

// Допустимые переходы статусов — отражает validateStatusTransition в backend.
const STATUS_TRANSITIONS: Record<PaymentStatus, ReadonlyArray<PaymentStatus>> = {
  [PaymentStatus.PENDING]: [PaymentStatus.CONFIRMED, PaymentStatus.REJECTED],
  [PaymentStatus.CONFIRMED]: [PaymentStatus.REFUNDED],
  [PaymentStatus.REJECTED]: [],
  [PaymentStatus.REFUNDED]: [],
  [PaymentStatus.PARTIALLY_REFUNDED]: [],
};

function getAvailableActions(
  status: PaymentStatus,
  provider: PaymentProvider,
): PaymentActionConfig[] {
  const allowed = STATUS_TRANSITIONS[status] ?? [];
  return ALL_PAYMENT_ACTIONS.filter((action) => {
    if (!allowed.includes(action.target)) { return false; }
    // У эквайринговых провайдеров confirm/reject делает только webhook —
    // вместо них админ пользуется кнопкой «Синхронизировать статус».
    if (ACQUIRING_PROVIDERS.has(provider)
      && (action.target === PaymentStatus.CONFIRMED || action.target === PaymentStatus.REJECTED)) {
      return false;
    }
    return true;
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) { return '\u2014'; }
  return new Date(value).toLocaleString('ru-RU');
}

function formatDate(value: string | null | undefined): string {
  if (!value) { return '\u2014'; }
  return new Date(value).toLocaleDateString('ru-RU');
}

function formatMulticardDateTime(value: string | null | undefined): string | null {
  if (!value) { return null; }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) { return null; }
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatAmountTiyin(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) { return null; }
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
  return `${formatted} сум`;
}

/**
 * Форматирует пару валют + курс в человекочитаемом виде.
 * Если курс < 1 — инвертирует пару, чтобы не показывать «0.00008266»,
 * а более понятное «1 USD = 12 145.65 UZS». Возвращает то направление,
 * в котором левая часть = 1, а правая — число >= 1.
 */
function formatExchangeRate(fromCurrency: string, toCurrency: string, rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) {
    return '—';
  }
  const formatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 6 });
  if (rate >= 1) {
    return `1 ${fromCurrency} = ${formatter.format(rate)} ${toCurrency}`;
  }
  return `1 ${toCurrency} = ${formatter.format(1 / rate)} ${fromCurrency}`;
}

function formatCurrency(amount: string, currency: string): string {
  const num = parseFloat(amount);
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
  return `${formatted} ${currency}`;
}

function isNonEmpty<T>(value: T | null | undefined | ''): value is T {
  return value !== null && value !== undefined && value !== '';
}

function getStatusBadge(status: string) {
  const config = STATUS_CONFIG[status.toUpperCase()];
  return (
    <Badge variant="outline" className={config?.className}>
      {config?.label ?? status}
    </Badge>
  );
}

function getProviderBadge(provider: string) {
  const config = PROVIDER_CONFIG[provider.toUpperCase()];
  return (
    <Badge variant="outline" className={config?.className}>
      {config?.label ?? provider}
    </Badge>
  );
}

function getUserName(payment: Payment): string {
  if (!payment.user) { return `#${payment.userId}`; }
  const name = [payment.user.firstName, payment.user.lastName].filter(Boolean).join(' ');
  return name || (payment.user.username ? `@${payment.user.username}` : `#${payment.userId}`);
}

interface CopyableFieldProps {
  value: string;
  mono?: boolean;
}

function CopyableField({ value, mono = true }: CopyableFieldProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify.success('Скопировано в буфер обмена');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      notify.error('Не удалось скопировать');
    }
  };

  return (
    <div className="flex items-start gap-2">
      <p className={`font-medium break-all ${mono ? 'font-mono' : ''}`}>{value}</p>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={handleCopy}
        aria-label="Скопировать"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

interface MulticardFieldRowProps {
  label: string;
  children: ReactNode;
}

function MulticardFieldRow({ label, children }: MulticardFieldRowProps) {
  return (
    <div>
      <span className="text-muted-foreground">{label}</span>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  );
}

interface MulticardDetailsCardProps {
  payload: MulticardPaymentPayload;
}

function MulticardDetailsCard({ payload }: MulticardDetailsCardProps) {
  const paymentTime = formatMulticardDateTime(payload.payment_time);
  const refundTime = formatMulticardDateTime(payload.refund_time);
  const amountFormatted = formatAmountTiyin(payload.amount_tiyin);
  const psKey = payload.ps ? payload.ps.toLowerCase() : null;
  const psLabel = psKey ? (PS_LABELS[psKey] ?? payload.ps ?? null) : null;

  const rows: ReactNode[] = [];

  if (isNonEmpty(payload.multicard_uuid)) {
    rows.push(
      <MulticardFieldRow key="multicard_uuid" label="Multicard UUID">
        <CopyableField value={payload.multicard_uuid} />
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.invoice_id)) {
    rows.push(
      <MulticardFieldRow key="invoice_id" label="Invoice ID">
        <CopyableField value={payload.invoice_id} />
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.store_id)) {
    rows.push(
      <MulticardFieldRow key="store_id" label="Store ID">
        <p className="font-medium font-mono">{String(payload.store_id)}</p>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.checkout_url)) {
    rows.push(
      <MulticardFieldRow key="checkout_url" label="Checkout URL">
        <a
          href={payload.checkout_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline break-all"
        >
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          <span className="break-all">{payload.checkout_url}</span>
        </a>
      </MulticardFieldRow>,
    );
  }

  if (amountFormatted !== null) {
    rows.push(
      <MulticardFieldRow key="amount_tiyin" label="Сумма (tiyin)">
        <p className="font-medium font-mono">{amountFormatted}</p>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.card_token)) {
    rows.push(
      <MulticardFieldRow key="card_token" label="Токен карты">
        <CopyableField value={payload.card_token} />
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.card_pan)) {
    rows.push(
      <MulticardFieldRow key="card_pan" label="PAN карты">
        <p className="font-medium font-mono">{payload.card_pan}</p>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(psLabel)) {
    rows.push(
      <MulticardFieldRow key="ps" label="Платёжная система">
        <Badge variant="outline" className="uppercase">{psLabel}</Badge>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.phone)) {
    rows.push(
      <MulticardFieldRow key="phone" label="Телефон">
        <p className="font-medium font-mono">{payload.phone}</p>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.billing_id)) {
    rows.push(
      <MulticardFieldRow key="billing_id" label="Billing ID">
        <p className="font-medium font-mono break-all">{payload.billing_id}</p>
      </MulticardFieldRow>,
    );
  }

  if (paymentTime !== null) {
    rows.push(
      <MulticardFieldRow key="payment_time" label="Время оплаты">
        <p className="font-medium">{paymentTime}</p>
      </MulticardFieldRow>,
    );
  }

  if (refundTime !== null) {
    rows.push(
      <MulticardFieldRow key="refund_time" label="Время возврата">
        <p className="font-medium">{refundTime}</p>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.receipt_url)) {
    rows.push(
      <MulticardFieldRow key="receipt_url" label="Чек">
        <a
          href={payload.receipt_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
        >
          <Receipt className="h-3.5 w-3.5" />
          Открыть чек
        </a>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.webhook_status)) {
    rows.push(
      <MulticardFieldRow key="webhook_status" label="Статус webhook">
        <p className="font-medium">{payload.webhook_status}</p>
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.webhook_uuid)) {
    rows.push(
      <MulticardFieldRow key="webhook_uuid" label="Webhook UUID">
        <CopyableField value={payload.webhook_uuid} />
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.callback_uuid)) {
    rows.push(
      <MulticardFieldRow key="callback_uuid" label="Callback UUID">
        <CopyableField value={payload.callback_uuid} />
      </MulticardFieldRow>,
    );
  }

  if (isNonEmpty(payload.return_url)) {
    rows.push(
      <MulticardFieldRow key="return_url" label="Return URL">
        <CopyableField value={payload.return_url} mono={false} />
      </MulticardFieldRow>,
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="h-4 w-4" />
          Детали Multicard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{rows}</div>
      </CardContent>
    </Card>
  );
}

interface PaymentActionButtonProps {
  action: PaymentActionConfig;
  disabled: boolean;
  isPending: boolean;
  onConfirm: (action: PaymentActionConfig) => void;
  onOpenRefund?: () => void;
}

function PaymentActionButton({ action, disabled, isPending, onConfirm, onOpenRefund }: PaymentActionButtonProps) {
  const [open, setOpen] = useState(false);

  // Для возврата открываем отдельный RefundDialog с выбором полный/частичный
  if (action.target === PaymentStatus.REFUNDED) {
    return (
      <Button
        type="button"
        variant={action.buttonVariant}
        className={`${action.buttonClassName ?? ''} cursor-pointer`}
        disabled={disabled}
        onClick={() => onOpenRefund?.()}
      >
        {isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <span className="mr-2">{action.icon}</span>}
        {action.label}
      </Button>
    );
  }

  const handleConfirm = () => {
    setOpen(false);
    onConfirm(action);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant={action.buttonVariant}
        className={`${action.buttonClassName ?? ''} cursor-pointer`}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <span className="mr-2">{action.icon}</span>}
        {action.label}
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{action.dialogTitle}</AlertDialogTitle>
          <AlertDialogDescription>{action.dialogDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="cursor-pointer">Отмена</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} className={`${action.confirmClassName ?? ''} cursor-pointer`}>
            {action.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


export function PaymentsShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  const invalidate = useInvalidate();

  const paymentResult = useOne<Payment>({
    resource: 'payments',
    id: id ?? '',
  });

  const payment = paymentResult.result;
  const isLoading = paymentResult.query?.isLoading ?? false;

  const { mutate: updateStatus, mutation: updateMutation } = useCustomMutation();
  const isUpdating = updateMutation.isPending;

  const { mutate: syncFromProvider, mutation: syncMutation } = useCustomMutation();
  const isSyncing = syncMutation.isPending;

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);

  const handleAction = (action: PaymentActionConfig) => {
    if (!payment) { return; }
    updateStatus(
      {
        url: `/payments/${payment.id}/status`,
        method: 'patch',
        values: { status: action.target },
      },
      {
        onSuccess: () => {
          notify.success(action.successMessage);
          paymentResult.query?.refetch();
          invalidate({ resource: 'payments', invalidates: ['list', 'detail'] });
          invalidate({ resource: 'orders', invalidates: ['list', 'detail'] });
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : action.errorMessage;
          notify.error(message);
        },
      },
    );
  };

  const handleRefund = (refundAmount?: number) => {
    if (!payment) { return; }
    updateStatus(
      {
        url: `/payments/${payment.id}/status`,
        method: 'patch',
        values: {
          status: PaymentStatus.REFUNDED,
          ...(refundAmount !== undefined ? { refundAmount } : {}),
        },
      },
      {
        onSuccess: () => {
          notify.success(getRefundSuccessMessage(payment.provider));
          setRefundDialogOpen(false);
          paymentResult.query?.refetch();
          invalidate({ resource: 'payments', invalidates: ['list', 'detail'] });
          invalidate({ resource: 'orders', invalidates: ['list', 'detail'] });
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Не удалось оформить возврат';
          notify.error(message);
        },
      },
    );
  };

  const handleSyncFromProvider = () => {
    if (!payment) { return; }
    const providerLabel = PROVIDER_CONFIG[payment.provider]?.label ?? payment.provider;
    syncFromProvider(
      {
        url: `/payments/${payment.id}/sync-from-provider`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: (response) => {
          const data = response?.data as {
            providerStatus?: string | null;
            statusChanged?: boolean;
            note?: string | null;
            providerMessage?: string | null;
          } | undefined;

          const providerStatus = data?.providerStatus ?? null;
          const statusChanged = data?.statusChanged === true;
          const note = data?.note ?? null;

          if (providerStatus === null) {
            notify.info(note ?? `${providerLabel} не вернул данные по транзакции`);
          } else if (statusChanged) {
            notify.success(`Статус обновлён из ${providerLabel}: ${providerStatus}`);
          } else {
            notify.info(
              note
                ? `${providerLabel} статус: ${providerStatus}. ${note}`
                : `Статус в ${providerLabel}: ${providerStatus} (локально уже актуален)`,
            );
          }

          paymentResult.query?.refetch();
          invalidate({ resource: 'payments', invalidates: ['list', 'detail'] });
          invalidate({ resource: 'orders', invalidates: ['list', 'detail'] });
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Не удалось синхронизировать статус';
          notify.error(message);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Платёж не найден</p>
        <Button variant="link" onClick={() => go({ to: '/payments' })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const actions = getAvailableActions(payment.status, payment.provider);
  const isCloudPayments = payment.provider === PaymentProvider.CLOUDPAYMENTS;
  const isMulticard = payment.provider === PaymentProvider.MULTICARD;
  const showSyncButton = (isCloudPayments || isMulticard)
    && (payment.status === PaymentStatus.PENDING || payment.status === PaymentStatus.CONFIRMED);

  const syncButtonLabel = isCloudPayments
    ? 'Проверить статус в CloudPayments'
    : 'Синхронизировать статус';

  const syncButton = showSyncButton ? (
    <Button
      type="button"
      variant="outline"
      disabled={isSyncing || isUpdating}
      onClick={handleSyncFromProvider}
    >
      {isSyncing ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4 mr-2" />
      )}
      {syncButtonLabel}
    </Button>
  ) : null;

  const headerActions = actions.length > 0 || syncButton ? (
    <>
      {syncButton}
      {actions.map((action) => (
        <PaymentActionButton
          key={action.target}
          action={action}
          disabled={isUpdating || isSyncing}
          isPending={isUpdating}
          onConfirm={handleAction}
          onOpenRefund={() => setRefundDialogOpen(true)}
        />
      ))}
    </>
  ) : undefined;

  const multicardPayload =
    isMulticard && payment.providerPayload
      ? payment.providerPayload
      : null;

  const mainContent = (
    <div className="space-y-6">
      {/* Информация о платеже */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4" />
            Информация о платеже
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Сумма</span>
              <p className="font-medium font-mono text-lg">
                {formatCurrency(payment.amount, payment.currency)}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Валюта</span>
              <p className="font-medium">{payment.currency}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Статус</span>
              <div className="mt-1">{getStatusBadge(payment.status)}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Провайдер</span>
              <div className="mt-1">{getProviderBadge(payment.provider)}</div>
            </div>
            <div>
              <span className="text-muted-foreground">ID провайдера</span>
              <p className="font-medium font-mono">
                {payment.providerPaymentId ?? '\u2014'}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Дата оплаты</span>
              <p className="font-medium">{formatDateTime(payment.paidAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Детали Multicard */}
      {multicardPayload && <MulticardDetailsCard payload={multicardPayload} />}

      {/* Курс валют — показываем только если курс зафиксирован валидным значением */}
      {payment.fixedExchangeRate &&
        Number(payment.fixedExchangeRate) > 0 &&
        payment.fixedBaseCurrency &&
        payment.fixedBaseCurrency !== payment.currency && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ArrowLeftRight className="h-4 w-4" />
              Курс валют
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Курс обмена</span>
                <p className="font-medium font-mono">
                  {formatExchangeRate(payment.currency, payment.fixedBaseCurrency, Number(payment.fixedExchangeRate))}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Базовая валюта</span>
                <p className="font-medium">{payment.fixedBaseCurrency ?? '\u2014'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Сумма в базовой валюте</span>
                <p className="font-medium font-mono">
                  {payment.fixedAmountBase && payment.fixedBaseCurrency
                    ? formatCurrency(payment.fixedAmountBase, payment.fixedBaseCurrency)
                    : '\u2014'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Курс провайдера — фиксируется при создании платежа провайдером
          (CloudPayments/Multicard). Показываем как только курс есть, даже
          если платёж ещё PENDING — это позволяет видеть, что курс зафиксирован. */}
      {payment.providerExchangeRate &&
        Number(payment.providerExchangeRate) > 0 &&
        payment.orderAmount &&
        payment.orderCurrency &&
        payment.orderCurrency !== payment.currency && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ArrowLeftRight className="h-4 w-4" />
              Курс провайдера (на момент создания платежа)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
              <div>
                <span className="text-muted-foreground">Сумма заказа</span>
                <p className="font-medium font-mono">
                  {formatCurrency(payment.orderAmount, payment.orderCurrency)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Курс</span>
                <p className="font-medium font-mono">
                  {formatExchangeRate(payment.orderCurrency, payment.currency, Number(payment.providerExchangeRate))}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Источник</span>
                <p className="font-medium">{payment.providerExchangeRateSource ?? '—'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Получен</span>
                <p className="font-medium">{formatDateTime(payment.providerExchangeRateFetchedAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Доказательство оплаты (legacy одиночный proofUrl) */}
      {payment.proofUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ImageIcon className="h-4 w-4" />
              Доказательство оплаты
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <img
                src={payment.proofUrl}
                alt="Доказательство оплаты"
                className="max-w-full max-h-96 rounded-lg border object-contain"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (payment.proofUrl) { window.open(payment.proofUrl, '_blank'); } }}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Открыть в новой вкладке
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Документы платежа (множественные, через FileUsage) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Paperclip className="h-4 w-4" />
            Документы
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PaymentDocumentsUpload paymentId={payment.id} />
        </CardContent>
      </Card>

      {/* Debug & Forensic Trail: чеки 54-ФЗ, события, webhook'и, export */}
      <PaymentDebugPanel paymentId={payment.id} orderId={payment.orderId ?? null} />
    </div>
  );

  const sidebarContent = (
    <div className="space-y-4">
      {/* Пользователь */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserIcon className="h-4 w-4" />
            Пользователь
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">Имя</span>
              <p className="font-medium">{getUserName(payment)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Username</span>
              <p className="font-medium">
                {payment.user?.username ? `@${payment.user.username}` : '\u2014'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full cursor-pointer"
              onClick={() => go({ to: `/users/show/${payment.userId}` })}
            >
              <UserIcon className="h-3.5 w-3.5 mr-2" />
              Перейти к пользователю
              <ExternalLink className="h-3 w-3 ml-auto opacity-60" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Заказ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCart className="h-4 w-4" />
            Заказ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">Номер заказа</span>
              <p className="font-medium">#{payment.orderId}</p>
            </div>
            {payment.order && (
              <div>
                <span className="text-muted-foreground">Сумма заказа</span>
                <p className="font-medium font-mono">
                  {formatCurrency(payment.order.total, payment.order.currency)}
                </p>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full cursor-pointer"
              onClick={() => go({ to: `/orders/show/${payment.orderId}` })}
            >
              <ShoppingCart className="h-3.5 w-3.5 mr-2" />
              Перейти к заказу
              <ExternalLink className="h-3 w-3 ml-auto opacity-60" />
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );

  return (
    <div className="space-y-6">
      <FormHeader
        title={`Платёж #${payment.id}`}
        onBack={() => go({ to: '/payments' })}
        titleAddon={getStatusBadge(payment.status)}
        actions={headerActions}
        sticky
      />

      {/* Аналитические карточки */}
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Сумма"
          value={formatCurrency(payment.amount, payment.currency)}
          description={payment.fixedAmountBase && payment.fixedBaseCurrency && payment.currency !== payment.fixedBaseCurrency
            ? `${formatCurrency(payment.fixedAmountBase, payment.fixedBaseCurrency)}`
            : undefined}
          icon={<CreditCard className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Статус"
          value={getStatusBadge(payment.status)}
        />
        <EntityAnalyticsCard
          title="Провайдер"
          value={getProviderBadge(payment.provider)}
        />
        <EntityAnalyticsCard
          title="Дата оплаты"
          value={payment.paidAt ? formatDate(payment.paidAt) : 'Не оплачен'}
          icon={<Clock className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      <TwoColumnLayout main={mainContent} sidebar={sidebarContent} />

      <RefundDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        paymentAmount={Number(payment.amount)}
        currency={payment.currency}
        provider={payment.provider}
        isPending={isUpdating}
        onConfirm={handleRefund}
      />
    </div>
  );
}
